import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface InviteToInterviewParams {
  responseIds: string[];
  testId: string;
}

export const useInterviewInvitation = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const inviteToInterview = useMutation({
    mutationFn: async ({ responseIds, testId }: InviteToInterviewParams) => {
      const { data, error } = await supabase.functions.invoke('send-interview-invitation', {
        body: { responseIds, testId }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data, variables) => {
      const { successCount, failureCount } = data;
      
      if (successCount > 0) {
        toast({
          title: "Interview invitations sent",
          description: `Successfully sent ${successCount} invitation${successCount > 1 ? 's' : ''}${failureCount > 0 ? `, ${failureCount} failed` : ''}`,
        });
      } else {
        toast({
          title: "No invitations sent",
          description: "No eligible candidates found or all invitations failed",
          variant: "destructive",
        });
      }

      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['test-responses'] });
      queryClient.invalidateQueries({ queryKey: ['test', variables.testId] });
    },
    onError: (error: any) => {
      console.error('Error sending interview invitations:', error);
      toast({
        title: "Failed to send invitations",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    },
  });

  return {
    inviteToInterview,
    isInviting: inviteToInterview.isPending,
  };
};